// Same voice in 2D and 3D (26 Oct 2026): the 2D avatar speaks with its 3D
// Kokoro voice (af_bella, am_fenrir, ...) through the site's own speech route,
// POST /avatarSpeech/synthesize, which picks the voice from the avatar name
// (proxy/avatarSpeech.mjs, the same table as AVATAR_OBJECTS). It resolves false
// when that isn't possible (flag off, no speech server, error, stopped), and
// the caller then uses the browser voice as before.
//
// Sentence by sentence (27 Oct 2026, REACT_APP_2D_VOICE_SENTENCES_ENABLED): every
// sentence is requested at once (the server's queue is first in, first out, so
// the first sentence is synthesized first) and played in order, so speech starts
// after one sentence instead of the whole reply. 3D already does this (HeadTTS
// splitSentences).
export const AVATAR_VOICE_2D_ENABLED = () => process.env.REACT_APP_2D_AVATAR_VOICE_ENABLED !== "false";
const SENTENCES_ENABLED = () => process.env.REACT_APP_2D_VOICE_SENTENCES_ENABLED !== "false";

let _current = null; // { audio, aborted }

export function stopAvatarVoice() {
  if (!_current) return;
  _current.aborted = true;
  try { _current.audio && _current.audio.pause(); } catch (_) {}
  _current = null;
}

// "Hi Tester! I'm Benjamin. How may I help?" -> one part per sentence (the same
// dividers as HeadTTS: ". ", "! ", "? "). A tiny part ("Hi!") joins the next one.
export function splitSentences(text) {
  const raw = String(text || "").trim().split(/(?<=[.!?])\s+/).filter(Boolean);
  const parts = [];
  for (const s of raw) {
    if (parts.length && parts[parts.length - 1].length < 12) parts[parts.length - 1] += ` ${s}`;
    else parts.push(s);
  }
  return parts;
}

const fetchAudio = async (text, avatar) => {
  try {
    const res = await fetch("/avatarSpeech/synthesize", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, avatar }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.audioBase64 ? body.audioBase64 : null;
  } catch (_) {
    return null;
  }
};

// Plays one clip; resolves when it has started (true) or could not (false),
// and calls ended() once it finishes.
const startClip = (job, base64, ended) => {
  const audio = new Audio(`data:audio/wav;base64,${base64}`);
  job.audio = audio;
  audio.onended = ended;
  audio.onerror = ended;
  return Promise.resolve().then(() => audio.play()).then(() => true, () => false);
};

export async function speakWithAvatarVoice(text, avatar, { onStart, onEnd } = {}) {
  stopAvatarVoice();
  if (!AVATAR_VOICE_2D_ENABLED() || !text || typeof fetch !== "function" || typeof Audio !== "function") return false;
  const job = { audio: null, aborted: false };
  _current = job;
  const parts = SENTENCES_ENABLED() ? splitSentences(text) : [String(text)];
  const clips = parts.map((p) => fetchAudio(p, avatar));
  const finish = () => { if (_current === job) _current = null; onEnd && onEnd(); };

  const first = await clips[0];
  if (job.aborted) return true; // stopped while synthesizing: say nothing
  if (!first) { if (_current === job) _current = null; return false; }
  let next = 1;
  const playNext = async () => {
    while (next < clips.length) {
      const clip = await clips[next++];
      if (job.aborted) return;
      if (clip && await startClip(job, clip, playNext)) return; // a missing sentence is skipped
      if (job.aborted) return;
    }
    finish();
  };
  if (!(await startClip(job, first, playNext))) {
    // Autoplay refused (no tap yet): let the browser voice try instead.
    if (_current === job) _current = null;
    return job.aborted;
  }
  if (job.aborted) return true;
  onStart && onStart();
  return true;
}
